import { EditorState } from 'draft-js';
import { convertFromHTML } from 'draft-convert';
import { CompanySizes, ThirdPartyCategories } from 'src/modules/3rd-party-management/constants';

export const THIRD_PARTY_TYPE_OPTIONS = [
    { value: 'company', label: 'Company' },
    { value: 'individual', label: 'Individual' },
];

export const SEED_FIELD_KEYS = ['type', 'name', 'country', 'subType'];

const buildCompanySizeOptions = () => {
    return Object.keys(CompanySizes).map((sizeKey) => ({
        value: sizeKey,
        label: CompanySizes[sizeKey]?.title,
    }));
};

const sharedLocationFields = {
    country: {
        key: 'country',
        type: 'country',
        labelKey: 'Country',
        required: true,
        readOnly: true,
        section: 'location',
    },
    'location.city': {
        key: 'location.city',
        type: 'text',
        labelKey: 'City',
        required: true,
        section: 'location',
    },
    'location.postalCode': {
        key: 'location.postalCode',
        type: 'text',
        labelKey: 'Postal Code',
        required: true,
        section: 'location',
    },
    'location.address': {
        key: 'location.address',
        type: 'text',
        labelKey: 'Street, Number',
        required: true,
        section: 'location',
    },
};

const getSeedInputFields = (type) => {
    const nameLabel = type === 'company' ? 'Company Name' : 'Name';

    return [
        {
            key: 'type',
            type: 'thirdPartyType',
            labelKey: 'Third Party Type',
            required: true,
        },
        {
            key: 'name',
            type: 'text',
            labelKey: nameLabel,
            required: true,
        },
        {
            key: 'country',
            type: 'country',
            labelKey: 'Country',
            required: true,
        },
        {
            key: 'subType',
            type: 'subType',
            labelKey: 'Third Party Category',
            required: true,
        },
        {
            key: 'customSubType',
            type: 'text',
            labelKey: 'Other Category',
            required: true,
            visibleWhen: ({ subType }) => subType === 'other',
        },
    ];
};

export const TYPE_FIELD_DEFINITIONS = {
    company: {
        sections: [
            { key: 'general', titleKey: 'General Information' },
            { key: 'location', titleKey: 'Location' },
        ],
        fields: {
            localLanguageFullName: {
                key: 'localLanguageFullName',
                type: 'text',
                labelKey: 'Company Name in Local Language',
                section: 'general',
            },
            shortName: {
                key: 'shortName',
                type: 'text',
                labelKey: 'Short Name',
                section: 'general',
            },
            companySize: {
                key: 'companySize',
                type: 'companySize',
                labelKey: 'Company Size',
                required: true,
                section: 'general',
                options: buildCompanySizeOptions(),
            },
            vatNumber: {
                key: 'vatNumber',
                type: 'vatNumber',
                labelKey: 'VAT ID',
                section: 'general',
            },
            registrationNumber: {
                key: 'registrationNumber',
                type: 'text',
                labelKey: 'Registration Number',
                section: 'general',
            },
            brands: {
                key: 'brands',
                type: 'text',
                labelKey: 'Brands',
                section: 'general',
            },
            websiteUrl: {
                key: 'websiteUrl',
                type: 'url',
                labelKey: 'Website',
                section: 'general',
            },
            description: {
                key: 'description',
                type: 'richText',
                labelKey: 'Description',
                section: 'general',
            },
            ...sharedLocationFields,
        },
    },
    individual: {
        sections: [
            { key: 'personal', titleKey: 'Personal Details' },
            { key: 'location', titleKey: 'Location' },
        ],
        fields: {
            localLanguageFullName: {
                key: 'localLanguageFullName',
                type: 'text',
                labelKey: 'Name in Local Language',
                section: 'personal',
            },
            shortName: {
                key: 'shortName',
                type: 'text',
                labelKey: 'Nickname / AKA',
                section: 'personal',
            },
            individualDateOfBirth: {
                key: 'individualDateOfBirth',
                type: 'date',
                labelKey: 'Date of Birth',
                section: 'personal',
            },
            individualBirthPlaceCountryId: {
                key: 'individualBirthPlaceCountryId',
                type: 'country',
                labelKey: 'Country of Birth',
                section: 'personal',
            },
            individualTaxId: {
                key: 'individualTaxId',
                type: 'text',
                labelKey: 'Tax ID',
                section: 'personal',
            },
            individualNationalIdNumber: {
                key: 'individualNationalIdNumber',
                type: 'text',
                labelKey: 'National ID Number',
                section: 'personal',
            },
            description: {
                key: 'description',
                type: 'richText',
                labelKey: 'Description',
                section: 'personal',
            },
            ...Object.entries(sharedLocationFields).reduce((acc, [key, field]) => {
                acc[key] = {
                    ...field,
                    required: false,
                };
                return acc;
            }, {})
        },
    },
};

export const getFieldDefinitionsForType = (type) => {
    return TYPE_FIELD_DEFINITIONS[type] || TYPE_FIELD_DEFINITIONS.company;
};

export const getSubTypeOptions = (type) => {
    return ThirdPartyCategories[type] || [];
};

export const getSeedInputFieldDefinitions = (type, values) => {
    return getSeedInputFields(type).filter((field) => {
        if (typeof field.visibleWhen === 'function') {
            return field.visibleWhen(values);
        }

        return true;
    });
};

export const getGeneratedFormSections = (type, values) => {
    const definition = getFieldDefinitionsForType(type);

    return definition.sections
        .map((section) => ({
            ...section,
            fields: Object.values(definition.fields).filter((field) => field.section === section.key),
        }))
        .filter((section) => section.fields.length > 0);
};

export const hasSeedValues = (values) => {
    return SEED_FIELD_KEYS.every((key) => {
        if (key === 'country') {
            return !!values.country?.value;
        }

        return !!values[key];
    }) && (values.subType !== 'other' || !!values.customSubType);
};

export const getInitialFormValues = (type = 'company') => ({
    type,
    name: '',
    country: null,
    subType: '',
    customSubType: '',
    localLanguageFullName: '',
    shortName: '',
    companySize: '0-10',
    vatNumber: '',
    registrationNumber: '',
    brands: '',
    websiteUrl: '',
    description: EditorState.createEmpty(),
    individualDateOfBirth: null,
    individualBirthPlaceCountryId: null,
    individualTaxId: '',
    individualNationalIdNumber: '',
    location: {
        city: '',
        address: '',
        postalCode: '',
    },
});

export const getNestedValue = (values, path) => {
    return path.split('.').reduce((current, key) => current?.[key], values);
};

export const setNestedValue = (values, path, value) => {
    const keys = path.split('.');
    const result = { ...values };
    let current = result;

    keys.forEach((key, index) => {
        if (index === keys.length - 1) {
            current[key] = value;
            return;
        }

        current[key] = { ...(current[key] || {}) };
        current = current[key];
    });

    return result;
};

const toEditorState = (content) => {
    if (!content) {
        return EditorState.createEmpty();
    }

    if (typeof content === 'string') {
        return EditorState.createWithContent(convertFromHTML(content));
    }

    return content;
};

// System/seed fields that are never overwritten by the AI response.
const AI_PRESERVED_FIELD_KEYS = ['type', 'subType', 'customSubType'];

export const mapAiResponseToFormValues = (type, currentValues, aiData, countriesList = []) => {
    if (!aiData) {
        return currentValues;
    }

    let nextValues = { ...currentValues };

    const assignValue = (key, value) => {
        // Partial merge: only apply values the AI actually returned.
        if (value === undefined || value === null || value === '') {
            return;
        }

        if (AI_PRESERVED_FIELD_KEYS.includes(key)) {
            return;
        }

        if (key.includes('.')) {
            nextValues = setNestedValue(nextValues, key, value);
            return;
        }

        nextValues[key] = value;
    };

    const commonMappings = {
        name: aiData.name,
        localLanguageFullName: aiData.localLanguageFullName,
        shortName: aiData.shortName,
        'location.city': aiData.location?.city,
        'location.address': aiData.location?.address,
        'location.postalCode': aiData.location?.postalCode,
    };

    Object.entries(commonMappings).forEach(([key, value]) => assignValue(key, value));

    if (aiData.description) {
        nextValues.description = toEditorState(aiData.description);
    }

    if (aiData.countryId && countriesList.length) {
        const matchedCountry = countriesList.find((country) => country.value === aiData.countryId);

        if (matchedCountry) {
            nextValues.country = matchedCountry;
        }
    }

    if (type === 'company') {
        assignValue('companySize', aiData.companySize);
        assignValue('vatNumber', aiData.vatNumber);
        assignValue('registrationNumber', aiData.registrationNumber);
        assignValue('websiteUrl', aiData.websiteUrl);
        assignValue('brands', aiData.brands);
    }

    if (type === 'individual') {
        assignValue('individualTaxId', aiData.individualTaxId);
        assignValue('individualNationalIdNumber', aiData.individualNationalIdNumber);

        if (aiData.individualDateOfBirth) {
            assignValue('individualDateOfBirth', aiData.individualDateOfBirth);
        }

        if (aiData.individualBirthPlaceCountryId && countriesList.length) {
            const birthCountry = countriesList.find(
                (country) => country.value === aiData.individualBirthPlaceCountryId
            );

            if (birthCountry) {
                nextValues.individualBirthPlaceCountryId = birthCountry;
            }
        }
    }

    // Always keep the user's system/seed selections after the merge.
    AI_PRESERVED_FIELD_KEYS.forEach((key) => {
        nextValues[key] = currentValues[key];
    });

    return nextValues;
};

// Form fields whose AI value is remembered, so the ones the user keeps are saved as AI-generated
// (the supplier panel then shows the AI icon next to them). Keys match the panel's aiFieldStatus keys.
const AI_TRACKED_FIELDS = {
    company: ['name', 'localLanguageFullName', 'shortName', 'companySize', 'vatNumber', 'registrationNumber', 'websiteUrl', 'brands', 'description'],
    individual: ['name', 'localLanguageFullName', 'shortName', 'description'],
};
const AI_LOCATION_FIELDS = ['location.city', 'location.address', 'location.postalCode'];

const normalizeComparable = (value) => {
    // The description is an editor state in the form and plain text / HTML in the AI response
    if (value?.getCurrentContent) {
        return value.getCurrentContent().getPlainText().trim();
    }

    return String(value ?? '').replace(/<[^>]*>/g, '').trim();
};

export const getAiProvidedValues = (type, aiData) => {
    if (!aiData) {
        return {};
    }

    const fromAi = {
        ...aiData,
        'location.city': aiData.location?.city,
        'location.address': aiData.location?.address,
        'location.postalCode': aiData.location?.postalCode,
    };

    return [...(AI_TRACKED_FIELDS[type] || []), ...AI_LOCATION_FIELDS].reduce((acc, key) => {
        const value = normalizeComparable(fromAi[key]);
        if (value !== '') {
            acc[key] = value;
        }
        return acc;
    }, {});
};

// Fields still holding exactly what the AI returned, and whether the address is still the AI's
export const getAiGeneratedFields = (type, values, aiProvidedValues) => {
    const isUnchanged = (key) => key in aiProvidedValues
        && normalizeComparable(getNestedValue(values, key)) === aiProvidedValues[key];

    const fields = (AI_TRACKED_FIELDS[type] || []).filter(isUnchanged);
    const aiLocationKeys = AI_LOCATION_FIELDS.filter((key) => key in aiProvidedValues);
    const isAiLocation = aiLocationKeys.length > 0 && aiLocationKeys.every(isUnchanged);

    return { fields, isAiLocation };
};

export const buildAiRequestPayload = (values) => ({
    name: values.name,
    country: values.country?.baseLabel || values.country?.label,
    type: values.type,
    subType: values.subType === 'other' ? values.customSubType : values.subType,
});
